import type React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { emojiOptions } from "@/lib/emoji/shortcodeToUnicode";

interface EmojiPickerPanelProps {
  onSelect: (emoji: string) => void;
  recentEmojis: string[];
}

const MAX_VISIBLE_EMOJIS = 180;

export const EmojiPickerPanel: React.FC<EmojiPickerPanelProps> = ({
  onSelect,
  recentEmojis,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const normalized = query.toLowerCase().trim();
    if (!normalized) {
      return emojiOptions.slice(0, MAX_VISIBLE_EMOJIS);
    }

    return emojiOptions
      .filter(({ label }) => label.includes(normalized))
      .slice(0, MAX_VISIBLE_EMOJIS);
  }, [query]);

  return (
    <div className="w-full space-y-2">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("common.searchEmoji")}
        className="h-9 w-full rounded-md border border-gray-300 px-2 text-sm outline-none focus:border-rw-sub-deep"
      />

      {recentEmojis.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-medium text-gray-500">
            {t("common.recentEmojis")}
          </div>
          <div className="grid grid-cols-8 gap-1">
            {recentEmojis.slice(0, 24).map((emoji) => (
              <button
                key={`recent-${emoji}`}
                type="button"
                className="rounded p-1 text-xl hover:bg-gray-100"
                onClick={() => onSelect(emoji)}
                aria-label={`${t("common.emoji")} ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="grid max-h-64 grid-cols-8 gap-1 overflow-y-auto pr-1">
          {filtered.length === 0 && (
            <p className="col-span-8 py-6 text-center text-sm text-gray-500">
              {t("common.noEmojiResults")}
            </p>
          )}
          {filtered.map(({ emoji, label }) => (
            <button
              key={`${label}-${emoji}`}
              type="button"
              className="rounded p-1 text-xl hover:bg-gray-100"
              onClick={() => onSelect(emoji)}
              aria-label={`${t("common.emoji")} ${emoji}`}
              title={`${t("common.emoji")} ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
